import { useEffect } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { useQueryState } from 'nuqs';
import { activeSessionIdStore, changeToSession, sessionsStore, updateSessions } from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { hydraUiApi } from '@/services/api';
import { getLastVisitedRoute } from '@/services/session/last-visited-route';
import { hardRedirect } from '@/utils/hard-redirect';

/**
 * Logs the user out: posts to the `logout` endpoint, invalidates the session,
 * prunes the multi-account list, and redirects to login. Ported from the Ember
 * `logout` route (`auth.logout()` → `handleInvalidation`). Errors (including a
 * 401 from an already-expired session) are ignored — we log out client-side
 * regardless.
 *
 * There are two destinations:
 *
 * - By default, no message, and a return URL, so a new sign-in continues from the same page.
 * - With `?explicit=1`, a "Logged out" message and no return URL. The user chose to log out from a page
 *   they cannot use again, such as the account-closed notice.
 */
export default function LogoutPage() {
  const [explicit] = useQueryState('explicit');
  const isExplicit = explicit != null;

  useEffect(() => {
    void (async () => {
      try {
        await hydraUiApi.logout.create();
      } catch {
        // Ignore — the session is invalidated client-side below regardless.
      }
      // Clear the Journeys (Ember) feature/settings caches on logout, matching
      // Ember's session `handleInvalidation`. These localStorage keys are still
      // read by the embedded Journeys app, so a stale value must not survive a
      // logout into a different account.
      ['cio-features', 'cio-settings'].forEach((key) => window.localStorage.removeItem(key));
      session.invalidate();

      // Prune the just-logged-out account from the multi-account list, mirroring
      // Ember's session `handleInvalidation`: drop only the active slot (any
      // other signed-in accounts survive), switch the active slot to 0, and
      // persist the remaining set. Read the active id before `changeToSession(0)`
      // reassigns it. Without this the logged-out session lingers in
      // `sessionsStore`, so a fresh `/login` load would treat the user as a
      // returning session and stall on the auth-bootstrap loader.
      const invalidatedId = activeSessionIdStore.get();
      const remaining = sessionsStore.get().filter((s) => s.id !== invalidatedId);
      changeToSession(0);
      updateSessions(remaining);

      // An explicit log out asks for the "Logged out" confirmation and no origin.
      if (isExplicit) {
        hardRedirect('/login?loggedOut=1');
        return;
      }

      // Return the user to the page they were on after they log back in
      // (Ember's `handleInvalidation` "origin" branch). The `?origin=` param
      // carries the value across the full-document `hardRedirect` — the login
      // flow's `resolveRedirectTarget` reads it. (Ember also seeded the
      // in-memory attempted-transition store here, but that's wiped by the full
      // reload, so it's the query param that actually carries it.) With no
      // recorded route, fall through to a plain `/login`.
      const lastVisitedRoute = getLastVisitedRoute();
      if (lastVisitedRoute != null && lastVisitedRoute !== '') {
        hardRedirect(`/login?origin=${encodeURIComponent(lastVisitedRoute)}`);
      } else {
        hardRedirect('/login');
      }
    })();
  }, [isExplicit]);

  return <PageLoader label="Signing out…" />;
}
